import { cn } from "../../utils/classnames.js";
import { DrawerContext } from "../../contexts/drawer.js";
import { useContext } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { Checkbox } from "@base-ui/react/checkbox";
import { ChevronRightIcon } from "lucide-react";
import { Drawer } from "@base-ui/react/drawer";
import { Radio } from "@base-ui/react/radio";
import { RadioGroup } from "@base-ui/react/radio-group";
//#region src/components/atoms/drawer.tsx
const DRAWER_SWIPE_DIRECTION_BY_POSITION = {
	bottom: "down",
	left: "left",
	right: "right",
	top: "up"
};
const DrawerCreateHandle = Drawer.createHandle;
const Drawer$1 = ({ swipeDirection, position = "bottom", ...props }) => {
	return /* @__PURE__ */ jsx(DrawerContext.Provider, {
		value: { position },
		children: /* @__PURE__ */ jsx(Drawer.Root, {
			swipeDirection: swipeDirection ?? DRAWER_SWIPE_DIRECTION_BY_POSITION[position],
			...props
		})
	});
};
const DrawerPortal = Drawer.Portal;
const DrawerTrigger = (props) => {
	return /* @__PURE__ */ jsx(Drawer.Trigger, {
		"data-slot": "drawer-trigger",
		...props
	});
};
const DrawerClose = (props) => {
	return /* @__PURE__ */ jsx(Drawer.Close, {
		"data-slot": "drawer-close",
		...props
	});
};
const DrawerSwipeArea = ({ className, position: positionProp, ...props }) => {
	const { position: contextPosition } = useContext(DrawerContext);
	const position = positionProp ?? contextPosition;
	return /* @__PURE__ */ jsx(Drawer.SwipeArea, {
		className: cn("fixed z-50 touch-none", {
			"inset-x-0 bottom-0 h-8": position === "bottom",
			"inset-x-0 top-0 h-8": position === "top",
			"inset-y-0 left-0 w-8": position === "left",
			"inset-y-0 right-0 w-8": position === "right"
		}, className),
		"data-slot": "drawer-swipe-area",
		...props
	});
};
const DrawerBackdrop = ({ className, ...props }) => {
	return /* @__PURE__ */ jsx(Drawer.Backdrop, {
		className: cn(`
          inset-0 bg-black/32 backdrop-blur-sm absolute z-50
          opacity-[calc(1-var(--drawer-swipe-progress))] transition-opacity duration-450
          ease-[cubic-bezier(0.32,0.72,0,1)]
          data-ending-style:opacity-0
          data-ending-style:duration-[calc(var(--drawer-swipe-strength)*400ms)]
          data-starting-style:opacity-0
          data-swiping:duration-0
        `, className),
		"data-slot": "drawer-backdrop",
		...props
	});
};
const DrawerViewport = ({ className, position, variant = "default", ...props }) => {
	return /* @__PURE__ */ jsx(Drawer.Viewport, {
		className: cn(`
          inset-0 fixed z-50 touch-none [--bleed:calc(var(--spacing)*12)]
          [--inset:calc(var(--spacing)*0)]
        `, {
			"pt-12 grid grid-rows-[1fr_auto]": position === "bottom",
			"pb-12 grid grid-rows-[auto_1fr]": position === "top",
			"flex justify-start": position === "left",
			"flex justify-end": position === "right",
			"sm:[--inset:calc(var(--spacing)*4)] px-[--inset]": variant === "inset",
			"pt-[--inset]": variant === "inset" && position !== "bottom",
			"pb-[--inset]": variant === "inset" && position !== "top"
		}, className),
		"data-slot": "drawer-viewport",
		...props
	});
};
const DrawerHeader = ({ className, allowSelection = false, render, ...props }) => {
	const defaultProps = {
		className: cn(`
        gap-2 p-6
        in-[[data-slot=drawer-popup]:has([data-slot=drawer-panel])]:pb-3
        max-sm:pb-4
        flex flex-col
      `, { "cursor-default": !allowSelection }, className),
		"data-slot": "drawer-header"
	};
	return useRender({
		defaultTagName: "div",
		props: mergeProps(defaultProps, props),
		render: allowSelection ? /* @__PURE__ */ jsx(DrawerContent, { render }) : render
	});
};
const DrawerFooter = ({ className, variant = "default", allowSelection = true, render, ...props }) => {
	const defaultProps = {
		className: cn(`
        gap-2 px-6
        sm:flex-row sm:justify-end
        flex flex-col-reverse pb-[env(safe-area-inset-bottom,0px)]
      `, {
			"cursor-default": !allowSelection,
			"py-6 border-t": variant === "default",
			[`
          in-[[data-slot=drawer-popup]:has([data-slot=drawer-panel])]:pt-3
          pt-4 pb-[calc(env(safe-area-inset-bottom,0px)+var(--spacing)*6)]
        `]: variant === "bare"
		}, className),
		"data-slot": "drawer-footer"
	};
	return useRender({
		defaultTagName: "div",
		props: mergeProps(defaultProps, props),
		render: allowSelection ? /* @__PURE__ */ jsx(DrawerContent, { render }) : render
	});
};
const DrawerTitle = ({ className, ...props }) => {
	return /* @__PURE__ */ jsx(Drawer.Title, {
		className: cn("font-semibold text-xl leading-none", className),
		"data-slot": "drawer-title",
		...props
	});
};
const DrawerDescription = ({ className, ...props }) => {
	return /* @__PURE__ */ jsx(Drawer.Description, {
		className: cn("text-muted-foreground text-sm", className),
		"data-slot": "drawer-description",
		...props
	});
};
const DrawerBar = ({ className, position: positionProp, render, ...props }) => {
	const { position: contextPosition } = useContext(DrawerContext);
	const position = positionProp ?? contextPosition;
	const horizontal = position === "left" || position === "right";
	const defaultProps = {
		"aria-hidden": true,
		className: cn(`p-3 before:bg-input absolute flex touch-none items-center justify-center before:rounded-full`, {
			"inset-y-0 before:h-12 before:w-1": horizontal,
			"inset-x-0 before:h-1 before:w-12": !horizontal,
			"bottom-0": position === "top",
			"top-0": position === "bottom",
			"right-0": position === "left",
			"left-0": position === "right"
		}, className),
		"data-slot": "drawer-bar"
	};
	return useRender({
		defaultTagName: "div",
		props: mergeProps(defaultProps, props),
		render
	});
};
const DrawerContent = Drawer.Content;
const DrawerMenu = ({ className, render, ...props }) => {
	const defaultProps = {
		className: cn("-m-2 flex flex-col", className),
		"data-slot": "drawer-menu"
	};
	return useRender({
		defaultTagName: "nav",
		props: mergeProps(defaultProps, props),
		render
	});
};
const DrawerMenuItem = ({ className, variant = "default", render, disabled, ...props }) => {
	const defaultProps = {
		className: cn(`
        min-h-9 gap-2 rounded-sm px-2 py-1 text-base text-foreground
        hover:bg-accent hover:text-accent-foreground
        data-[variant=destructive]:text-destructive-foreground
        sm:min-h-8 sm:text-sm
        [&>svg:not-[class*='size-']]:size-4.5
        sm:[&>svg:not-[class*='size-']]:size-4
        [&>svg]:-mx-0.5
        flex w-full cursor-default items-center outline-none select-none
        disabled:pointer-events-none disabled:opacity-64
        [&>svg]:pointer-events-none [&>svg]:shrink-0
        [&>svg:not-[class*='opacity-']]:opacity-80
      `, className),
		"data-slot": "drawer-menu-item",
		"data-variant": variant,
		disabled,
		type: "button"
	};
	return useRender({
		defaultTagName: "button",
		props: mergeProps(defaultProps, props),
		render
	});
};
const DrawerMenuSeparator = ({ className, render, ...props }) => {
	const defaultProps = {
		className: cn("mx-2 my-1 bg-border h-px", className),
		"data-slot": "drawer-menu-separator"
	};
	return useRender({
		defaultTagName: "div",
		props: mergeProps(defaultProps, props),
		render
	});
};
const DrawerMenuGroup = ({ className, render, ...props }) => {
	const defaultProps = {
		className: cn("flex flex-col", className),
		"data-slot": "drawer-menu-group"
	};
	return useRender({
		defaultTagName: "div",
		props: mergeProps(defaultProps, props),
		render
	});
};
const DrawerMenuGroupLabel = ({ className, render, ...props }) => {
	const defaultProps = {
		className: cn("px-2 py-1.5 font-medium text-muted-foreground text-xs", className),
		"data-slot": "drawer-menu-group-label"
	};
	return useRender({
		defaultTagName: "div",
		props: mergeProps(defaultProps, props),
		render
	});
};
const DrawerMenuTrigger = ({ className, children, ...props }) => {
	return /* @__PURE__ */ jsxs(DrawerTrigger, {
		className: cn(`
          min-h-9 gap-2 rounded-sm px-2 py-1 text-base text-foreground
          hover:bg-accent hover:text-accent-foreground
          sm:min-h-8 sm:text-sm
          [&_svg:not-[class*='size-']]:size-4.5
          sm:[&_svg:not-[class*='size-']]:size-4
          flex w-full cursor-default items-center outline-none select-none
          [&_svg]:pointer-events-none [&_svg]:shrink-0
        `, className),
		"data-slot": "drawer-menu-trigger",
		...props,
		children: [children, /* @__PURE__ */ jsx(ChevronRightIcon, { className: "-me-0.5 ms-auto opacity-80" })]
	});
};
const DrawerMenuCheckboxItem = ({ className, children, checked, defaultChecked, onCheckedChange, variant = "default", disabled, render, ...props }) => {
	return /* @__PURE__ */ jsx(Checkbox.Root, {
		checked,
		className: cn(`
          min-h-9 gap-2 rounded-sm px-2 py-1 text-base text-foreground
          hover:bg-accent hover:text-accent-foreground
          sm:min-h-8 sm:text-sm
          [&_svg:not-[class*='size-']]:size-4.5
          sm:[&_svg:not-[class*='size-']]:size-4
          [&_svg]:-mx-0.5
          grid w-full cursor-default items-center outline-none select-none
          data-disabled:pointer-events-none data-disabled:opacity-64
          [&_svg]:pointer-events-none [&_svg]:shrink-0
          [&_svg:not-[class*='opacity-']]:opacity-80
        `, {
			"gap-4 pe-1.5 grid-cols-[1fr_auto]": variant === "switch",
			"pe-4 grid-cols-[1rem_1fr]": variant !== "switch"
		}, className),
		"data-slot": "drawer-menu-checkbox-item",
		defaultChecked,
		disabled,
		onCheckedChange,
		render,
		...props,
		children: variant === "switch" ? /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("span", {
			className: "col-start-1",
			children
		}), /* @__PURE__ */ jsx(Checkbox.Indicator, {
			keepMounted: true,
			className: cn(`
              focus-visible:ring-ring focus-visible:ring-offset-background
              data-checked:bg-primary
              data-unchecked:bg-input
              sm:[--thumb-size:calc(var(--spacing)*3)]
              col-start-2 inline-flex h-[calc(var(--thumb-size)+2px)]
              w-[calc(var(--thumb-size)*2-2px)] shrink-0 items-center rounded-full p-px
              inset-shadow-[0_1px_color-black/4%] transition-[background-color,box-shadow]
              duration-200 outline-none [--thumb-size:calc(var(--spacing)*4)]
              focus-visible:ring-2 focus-visible:ring-offset-1
              data-disabled:opacity-64
            `),
			children: /* @__PURE__ */ jsx("span", { className: cn(`
                bg-background shadow-sm/5 pointer-events-none block aspect-square h-full origin-left
                rounded-[--thumb-size] will-change-transform
                [transition:translate_.15s,border-radius_.15s,scale_.1s_.1s,transform-origin_.15s]
                in-[[data-slot=drawer-menu-checkbox-item]:active]:rounded-[var(--thumb-size)/calc(var(--thumb-size)*1.10)]
                in-[[data-slot=drawer-menu-checkbox-item]:active]:not-data-disabled:scale-x-110
                in-[[data-slot=drawer-menu-checkbox-item][data-checked]]:origin-[var(--thumb-size)_50%]
                in-[[data-slot=drawer-menu-checkbox-item][data-checked]]:translate-x-[calc(var(--thumb-size)-4px)]
              `) })
		})] }) : /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(Checkbox.Indicator, {
			className: "col-start-1",
			children: /* @__PURE__ */ jsx("svg", {
				fill: "none",
				height: "24",
				stroke: "currentColor",
				strokeLinecap: "round",
				strokeLinejoin: "round",
				strokeWidth: "2",
				viewBox: "0 0 24 24",
				width: "24",
				xmlns: "http://www.w3.org/2000/svg",
				children: /* @__PURE__ */ jsx("path", { d: "M5.252 12.7 10.2 18.63 18.748 5.37" })
			})
		}), /* @__PURE__ */ jsx("span", {
			className: "col-start-2",
			children
		})] })
	});
};
const DrawerMenuRadioGroup = ({ className, ...props }) => {
	return /* @__PURE__ */ jsx(RadioGroup, {
		className: cn("flex flex-col", className),
		"data-slot": "drawer-menu-radio-group",
		...props
	});
};
const DrawerMenuRadioItem = ({ className, children, value, disabled, render, ...props }) => {
	return /* @__PURE__ */ jsxs(Radio.Root, {
		className: cn(`
          min-h-9 gap-2 rounded-sm px-2 py-1 text-base text-foreground
          hover:bg-accent hover:text-accent-foreground
          sm:min-h-8 sm:text-sm
          [&_svg:not-[class*='size-']]:size-4.5
          sm:[&_svg:not-[class*='size-']]:size-4
          [&_svg]:-mx-0.5
          grid w-full cursor-default items-center outline-none select-none
          data-disabled:pointer-events-none data-disabled:opacity-64
          [&_svg]:pointer-events-none [&_svg]:shrink-0
          [&_svg:not-[class*='opacity-']]:opacity-80
        `, "pe-4 grid-cols-[1rem_1fr] items-center", className),
		"data-slot": "drawer-menu-radio-item",
		disabled,
		render,
		value,
		...props,
		children: [/* @__PURE__ */ jsx(Radio.Indicator, {
			className: "col-start-1",
			children: /* @__PURE__ */ jsx("svg", {
				fill: "none",
				height: "24",
				stroke: "currentColor",
				strokeLinecap: "round",
				strokeLinejoin: "round",
				strokeWidth: "2",
				viewBox: "0 0 24 24",
				width: "24",
				xmlns: "http://www.w3.org/2000/svg",
				children: /* @__PURE__ */ jsx("path", { d: "M5.252 12.7 10.2 18.63 18.748 5.37" })
			})
		}), /* @__PURE__ */ jsx("span", {
			className: "col-start-2",
			children
		})]
	});
};
//#endregion
export { Drawer$1 as Drawer, DrawerBackdrop, DrawerBar, DrawerClose, DrawerContent, DrawerCreateHandle, DrawerDescription, DrawerFooter, DrawerHeader, DrawerMenu, DrawerMenuCheckboxItem, DrawerMenuGroup, DrawerMenuGroupLabel, DrawerMenuItem, DrawerMenuRadioGroup, DrawerMenuRadioItem, DrawerMenuSeparator, DrawerMenuTrigger, DrawerPortal, DrawerSwipeArea, DrawerTitle, DrawerTrigger, DrawerViewport };

//# sourceMappingURL=drawer.js.map